import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import { count, duration, trx } from "../format.js";
import type { OverviewView, OwnStakeView, ProviderAccount, QueueCounts } from "../api/types.js";
import { ErrorNote, Note, Panel, Pill, ScreenHeader, Spinner, Table, Td, Th } from "../ui.js";

/**
 * Monitoring (docs/product/mocks/admin-monitoring-mock.html, approved by the owner 2026-10-11): is the router
 * working, are providers funded, how the business is doing. One read, `GET /v1/admin/overview` (viewer, cached
 * 30 s), plus the queue counts. Every card says when its number was read. There are no "no data" tiles: a card
 * whose source does not exist is left out (API error rate: Prometheus only; blocks behind: the API reads no head).
 */

const POLL_MS = 30_000;
type Tone = "good" | "attention" | "bad" | "neutral";

/** Failure reasons in plain words; anything unlisted falls back to its slug with spaces. */
const FAILURE_TEXT: Record<string, string> = {
  insufficient_supply: "No provider had the energy",
  provider_unavailable: "Provider did not answer",
  delegation_failed: "Delegation did not land",
  receiver_not_activated: "Receiver address not activated",
  receiver_capacity_exceeded: "Receiver cannot take more",
  capacity_unavailable: "No capacity for this size",
  supply_paused: "Sales paused",
  price_above_limit: "Price above the customer's limit",
  quote_expired: "Price expired before payment",
  insufficient_funds: "Customer paid too little",
  chain_unavailable: "Chain did not answer",
  timeout: "Timed out",
  internal_error: "Our error",
  unknown: "Reason not recorded",
};
const failureText = (slug: string) => FAILURE_TEXT[slug] ?? slug.replace(/_/g, " ");

const hhmm = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(11, 16) : "—");
const pct = (part: number, whole: number) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)} %` : "—");
const ago = (iso: string | null, nowIso: string) =>
  iso === null ? null : Math.max(0, (Date.parse(nowIso) - Date.parse(iso)) / 1000);

const BORDER: Record<Tone, string> = {
  good: "border-line",
  attention: "border-warning/40",
  bad: "border-danger/50",
  neutral: "border-line",
};
const PILL_TEXT: Record<Tone, string> = { good: "ok", attention: "attention", bad: "act now", neutral: "" };

function Card({
  label,
  value,
  small,
  note,
  tone,
  asOf,
  wide = false,
  children,
}: {
  label: string;
  value?: string | undefined;
  small?: string | undefined;
  note?: string | undefined;
  tone?: Tone | undefined;
  asOf: string;
  wide?: boolean;
  children?: ComponentChildren;
}) {
  return (
    <div
      class={`flex min-w-0 flex-col gap-1.5 rounded-[20px] border bg-surface px-4 py-3.5 ${BORDER[tone ?? "neutral"]} ${wide ? "sm:col-span-2" : ""}`}
    >
      <div class="flex items-start justify-between gap-2">
        <span class="num text-[10.5px] tracking-[0.06em] text-ink-muted uppercase">{label}</span>
        {tone === undefined || tone === "neutral" ? null : <Pill value={PILL_TEXT[tone]} tone={tone} />}
      </div>
      {value === undefined ? null : (
        <div class="num text-[22px] leading-tight font-semibold tracking-[-0.02em]">
          {value}
          {small === undefined ? null : (
            <span class="ml-1 text-[11px] font-normal tracking-normal text-ink-muted">{small}</span>
          )}
        </div>
      )}
      {children}
      {note ? <div class="text-[11.5px] leading-relaxed text-ink-muted">{note}</div> : null}
      <div class="num mt-auto text-[10px] text-ink-muted opacity-75">as of {hhmm(asOf)} UTC</div>
    </div>
  );
}

function List({ rows }: { rows: Array<[string, string]> }) {
  return (
    <ul class="text-[12px]">
      {rows.map(([k, v]) => (
        <li key={k} class="flex justify-between gap-2 border-b border-line py-0.5 last:border-b-0">
          <span class="text-ink-muted">{k}</span>
          <span class="num">{v}</span>
        </li>
      ))}
    </ul>
  );
}

function Block({ title, lead, children }: { title: string; lead: string; children: ComponentChildren }) {
  return (
    <section class="mb-7">
      <div class="mb-2.5 flex flex-wrap items-baseline gap-2.5">
        <h2 class="text-[16px] font-extrabold tracking-[-0.01em]">{title}</h2>
        <p class="text-[12px] text-ink-muted">{lead}</p>
      </div>
      {children}
    </section>
  );
}

function Grid({ children }: { children: ComponentChildren }) {
  return <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>;
}

export function MonitoringScreen() {
  const overview = useResource((signal) => {
    void signal;
    return adminReads.overview();
  }, []);
  const queues = useResource((signal) => {
    void signal;
    return adminReads.queues();
  }, []);
  const reloadOverview = overview.reload;
  const reloadQueues = queues.reload;
  useEffect(() => {
    const timer = setInterval(() => {
      reloadOverview();
      reloadQueues();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [reloadOverview, reloadQueues]);

  const data = overview.data ?? null;
  const queueRows = queues.data?.available === true ? queues.data.data : null;
  const attention = data === null ? 0 : countAttention(data);

  return (
    <>
      <ScreenHeader
        title="Monitoring"
        lead="Is the router working, are providers funded, and how the business is doing — on one page. Green = fine, amber = look today, red = act now."
        action={
          data === null ? undefined : (
            <Pill
              value={attention === 0 ? "all fine" : `${attention} need${attention === 1 ? "s" : ""} attention`}
              tone={attention === 0 ? "good" : "attention"}
            />
          )
        }
      />
      {overview.loading && data === null ? (
        <Spinner />
      ) : overview.error !== null && data === null ? (
        <ErrorNote error={overview.error} onRetry={() => overview.reload()} />
      ) : data === null ? null : (
        <>
          <RouterBlock data={data} queueRows={queueRows} queuesAsOf={queues.data?.as_of ?? data.as_of} />
          <ProvidersBlock data={data} />
          <OwnPoolsBlock data={data} />
          <BusinessBlock data={data} />
        </>
      )}

      <Panel title="Queues" subtitle="Background jobs per worker queue: deliveries, checks, refunds.">
        {queues.loading && queues.data == null ? (
          <Spinner />
        ) : queues.error !== null ? (
          <ErrorNote error={queues.error} onRetry={() => queues.reload()} />
        ) : queueRows === null ? (
          <Note tone="warning">
            Queue counts are not available: {queues.data?.reason ?? "no reason given"}. The worker's queues live in
            Redis; check that the API can reach it.
          </Note>
        ) : (
          <QueueTable rows={queueRows} />
        )}
      </Panel>
    </>
  );
}

function failedTone(d: OverviewView): Tone {
  const share = d.router.orders_24h > 0 ? d.router.failed_24h / d.router.orders_24h : 0;
  return share >= 0.1 ? "bad" : d.router.failed_24h > 0 ? "attention" : "good";
}
function deliveryTone(d: OverviewView): Tone {
  const median = d.router.delivery.median_seconds;
  return median === null ? "neutral" : median > 120 ? "bad" : median > 30 ? "attention" : "good";
}
function scannerTone(d: OverviewView): Tone {
  const seconds = ago(d.router.scanner_last_run_at, d.as_of);
  return seconds === null ? "neutral" : seconds > 600 ? "bad" : seconds > 120 ? "attention" : "good";
}
function moneyTone(d: OverviewView): Tone {
  const free = d.business.wallets?.collector_free_sun;
  return free === null || free === undefined ? "neutral" : free < 0 ? "bad" : "good";
}
function countAttention(d: OverviewView): number {
  const tones: Tone[] = [
    failedTone(d),
    deliveryTone(d),
    scannerTone(d),
    moneyTone(d),
    d.providers.below_mark > 0 ? "attention" : "good",
    d.own_stake === null ? "good" : reclaimTone(d.own_stake),
  ];
  return tones.filter((t) => t === "attention" || t === "bad").length;
}

function RouterBlock({
  data,
  queueRows,
  queuesAsOf,
}: {
  data: OverviewView;
  queueRows: QueueCounts[] | null;
  queuesAsOf: string;
}) {
  const r = data.router;
  const last = r.last_order;
  const lastAgo = last === null ? null : ago(last.at, data.as_of);
  const lastDelivered =
    last === null || last.delegated_at === null ? null : (Date.parse(last.delegated_at) - Date.parse(last.at)) / 1000;
  const scannerAgo = ago(r.scanner_last_run_at, data.as_of);
  const waiting = queueRows === null ? null : queueRows.reduce((acc, q) => acc + q.waiting, 0);
  const failedJobs = queueRows === null ? null : queueRows.reduce((acc, q) => acc + q.failed, 0);
  const lastNote =
    last === null
      ? "No order has been placed yet."
      : `${last.amount === null ? "Order" : `${count(last.amount)} ${last.resource}`}${last.tier ? ` for ${last.tier}` : ""}; ${
          lastDelivered === null ? `now ${last.status}` : `delivered in ${duration(lastDelivered)}`
        }.`;
  return (
    <Block title="Router system" lead="Is the service taking and delivering orders.">
      <Grid>
        <Card
          label="Orders, last hour"
          value={count(r.orders_1h)}
          small={`· ${count(r.orders_24h)} in 24 h`}
          note="Every order placed, whatever happened to it next."
          asOf={data.as_of}
        />
        <Card
          label="Failed orders, 24 h"
          value={count(r.failed_24h)}
          small={`· ${pct(r.failed_24h, r.orders_24h)}`}
          tone={failedTone(data)}
          note={r.failed_by_reason.length === 0 ? "No order failed in the last 24 hours." : undefined}
          asOf={data.as_of}
        >
          {r.failed_by_reason.length === 0 ? null : (
            <List rows={r.failed_by_reason.slice(0, 4).map((f) => [failureText(f.slug), count(f.count)])} />
          )}
        </Card>
        <Card
          label="Delivery time, 24 h"
          value={duration(r.delivery.median_seconds)}
          small="median"
          tone={deliveryTone(data)}
          note={
            r.delivery.max_seconds === null
              ? "No order was delivered in the last 24 hours."
              : `From order placed to energy on the receiver. Slowest: ${duration(r.delivery.max_seconds)}; ${count(r.delivery.delivered_24h)} delivered.`
          }
          asOf={data.as_of}
        />
        <Card
          label="Last order"
          value={lastAgo === null ? "none" : duration(lastAgo)}
          small={lastAgo === null ? undefined : "ago"}
          note={lastNote}
          asOf={data.as_of}
        />
        {waiting === null ? null : (
          <Card
            label="Background jobs"
            value={count(waiting)}
            small={`waiting · ${count(failedJobs)} failed`}
            tone={failedJobs !== null && failedJobs > 0 ? "attention" : "good"}
            note="Deliveries, checks and refunds waiting to be picked up; failed ones are kept for inspection."
            asOf={queuesAsOf}
          />
        )}
        {scannerAgo === null ? null : (
          <Card
            label="Payment scanner"
            value={duration(scannerAgo)}
            small="since last pass"
            tone={scannerTone(data)}
            note="How long ago the scanner last read the chain for customer payments."
            asOf={data.as_of}
          />
        )}
      </Grid>
    </Block>
  );
}

function isBelow(row: ProviderAccount): boolean {
  return row.low_water_sun !== null && row.balance_sun !== null && row.balance_sun < row.low_water_sun;
}

function ProvidersBlock({ data }: { data: OverviewView }) {
  const p = data.providers;
  const below = p.data.filter((row) => row.enabled && isBelow(row));
  return (
    <Block title="Providers" lead="Where we buy energy: is there money on each, and what the auto top-up decided.">
      <Grid>
        <Card
          label="Top-ups today"
          value={count(p.topups_today.count)}
          small={`· ${trx(p.topups_today.sun, 1)}`}
          note="Money we sent to providers since midnight UTC."
          asOf={data.as_of}
        />
        <Card
          label="Providers below the mark"
          value={count(p.below_mark)}
          small={`of ${count(p.enabled)}`}
          tone={p.below_mark > 0 ? "attention" : "good"}
          note={
            below.length === 0
              ? "Every switched-on provider holds more than its low-water mark."
              : below
                  .map((row) => `${row.display_name ?? row.provider}: ${row.last_decision ?? "no top-up decision yet"}`)
                  .join("; ")
          }
          asOf={data.as_of}
        />
        <Card
          label="Healthy providers"
          value={count(p.healthy)}
          small={`of ${count(p.enabled)}${p.total > p.enabled ? ` · ${count(p.total - p.enabled)} off` : ""}`}
          tone={p.healthy < p.enabled ? "attention" : "good"}
          note="Switched-on providers whose last call succeeded."
          asOf={data.as_of}
        />
        <Card
          label="Our spend at providers, 24 h"
          value={trx(p.spend_24h_sun, 1)}
          note="What our customers' orders cost us there, not top-ups."
          asOf={data.as_of}
        />
      </Grid>
      <div class="mt-3">
        <Panel
          title="Per provider"
          subtitle="Credit = money left on our account there. Low-water = the level that triggers an auto top-up."
        >
          <Table
            rows={p.data.length}
            empty="No provider is configured."
            head={
              <tr>
                <Th>Provider</Th>
                <Th numeric>Credit</Th>
                <Th>Below mark?</Th>
                <Th numeric>Our spend, 24 h</Th>
                <Th>Last top-up</Th>
                <Th>Last decision</Th>
                <Th>Health</Th>
              </tr>
            }
          >
            {p.data.map((row) => (
              <tr key={row.provider}>
                <Td>{row.display_name ?? row.provider}</Td>
                <Td numeric>
                  {trx(row.balance_sun, 1)}
                  <span class="block text-[11px] text-ink-muted">
                    mark {row.low_water_sun === null ? "—" : trx(row.low_water_sun, 0)}
                  </span>
                </Td>
                <Td>
                  {!row.enabled ? (
                    <Pill value="off" tone="neutral" />
                  ) : isBelow(row) ? (
                    <Pill value="yes" tone="attention" />
                  ) : (
                    <Pill value="no" tone="good" />
                  )}
                </Td>
                <Td numeric>{trx(row.spend_24h_sun ?? 0, 1)}</Td>
                <Td mono>
                  {row.last_topup == null
                    ? "—"
                    : `${trx(row.last_topup.amount_sun, 0)} · ${row.last_topup.at.slice(5, 16).replace("T", " ")}`}
                </Td>
                <Td>
                  {row.last_decision ?? "—"}
                  {row.last_decision_at ? (
                    <span class="block text-[11px] text-ink-muted">{hhmm(row.last_decision_at)}</span>
                  ) : null}
                </Td>
                <Td>
                  {!row.enabled ? (
                    <Pill value="off" tone="neutral" />
                  ) : row.health_ok ? (
                    <Pill value="ok" tone="good" />
                  ) : (
                    <Pill value="failing" tone="bad" />
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>
    </Block>
  );
}

function reclaimTone(own: OwnStakeView): Tone {
  return own.reclaims.late > 0 ? "bad" : "good";
}

/**
 * Own-pool delivery (owner 2026-10-20): the fund wallets' stake, read from the worker's last chain sync, and our own
 * delegations and reclaims. With no staked fund wallet the API sends null and the placeholder of the approved mock
 * stays.
 */
function OwnPoolsBlock({ data }: { data: OverviewView }) {
  const own = data.own_stake;
  if (own === null) return <OwnPoolsPlaceholder />;
  const sum = (pick: (w: OwnStakeView["wallets"][number]) => number) => own.wallets.reduce((acc, w) => acc + pick(w), 0);
  const freeEnergy = own.wallets.every((w) => w.free_energy !== null) ? sum((w) => w.free_energy ?? 0) : null;
  const synced = own.wallets
    .map((w) => w.synced_at)
    .reduce<string | null>((acc, at) => (at === null ? acc : acc === null || at < acc ? at : acc), null);
  const syncedAsOf = synced ?? data.as_of;
  const several = own.wallets.length > 1;
  const { energy, bandwidth } = own.delegated;
  const r = own.reclaims;
  return (
    <Block title="Own-pool delivery" lead="Energy we delegate from our own staked wallets.">
      <Grid>
        <Card
          label="Staked"
          value={trx(sum((w) => w.staked_energy_sun), 0)}
          small="for energy"
          note={`${trx(sum((w) => w.staked_bandwidth_sun), 0)} for bandwidth. Gives ${count(sum((w) => w.energy_limit))} energy and ${count(sum((w) => w.net_limit))} bandwidth a day.`}
          asOf={syncedAsOf}
        >
          {several ? (
            <List rows={own.wallets.map((w) => [w.label ?? `${w.address.slice(0, 4)}…${w.address.slice(-4)}`, trx(w.staked_energy_sun, 0)])} />
          ) : null}
        </Card>
        <Card
          label="Free to delegate now"
          value={freeEnergy === null ? trx(sum((w) => w.free_sun), 0) : count(freeEnergy)}
          small={freeEnergy === null ? "of stake" : "energy"}
          note={`${freeEnergy === null ? "The energy figure needs a chain read that failed; this is the stake behind it." : `${trx(sum((w) => w.free_sun), 0)} of stake.`} Read from the chain at the wallet's last sync.`}
          asOf={syncedAsOf}
        >
          {several ? (
            <List
              rows={own.wallets.map((w) => [
                w.label ?? `${w.address.slice(0, 4)}…${w.address.slice(-4)}`,
                w.free_energy === null ? trx(w.free_sun, 0) : count(w.free_energy),
              ])}
            />
          ) : null}
        </Card>
        <Card
          label="Delegated now"
          value={count(energy.count)}
          small={`· ${count(energy.units)} energy`}
          note={`Customer orders on chain from our stake. Bandwidth: ${count(bandwidth.count)} · ${count(bandwidth.units)}.`}
          asOf={data.as_of}
        />
        <Card
          label="Reclaims"
          value={count(r.due_1h)}
          small={`due within 1 h · ${count(r.late)} late`}
          tone={reclaimTone(own)}
          note={
            r.late === 0
              ? "Every reclaim is on time."
              : `Late since ${hhmm(r.oldest_late_at)} UTC: the stake is still with the receiver. Late = ${duration(r.late_after_seconds)} past due.`
          }
          asOf={data.as_of}
        />
      </Grid>
    </Block>
  );
}

/** Before the first staked fund wallet (owner mock). */
function OwnPoolsPlaceholder() {
  return (
    <Block title="Own-pool delivery" lead="Energy we delegate from our own staked wallets.">
      <Grid>
        <div class="flex flex-col gap-1.5 rounded-[20px] border border-dashed border-line bg-surface px-4 py-3.5 sm:col-span-2">
          <div class="flex items-start justify-between gap-2">
            <span class="num text-[10.5px] tracking-[0.06em] text-ink-muted uppercase">Own pools</span>
            <Pill value="none yet" tone="neutral" />
          </div>
          <div class="text-[16px] font-semibold">No own pools yet</div>
          <div class="text-[11.5px] text-ink-muted">When the first pool is connected, this block will show:</div>
          <ul class="ml-4 list-disc text-[12px] text-ink-muted">
            <li>energy available to delegate right now, per pool;</li>
            <li>delegations active and how much energy they hold;</li>
            <li>reclaims due in the next hour, and any that are late.</li>
          </ul>
        </div>
      </Grid>
    </Block>
  );
}

function BusinessBlock({ data }: { data: OverviewView }) {
  const b = data.business;
  const rate = b.signups_7d > 0 ? b.signups_ordered_7d / b.signups_7d : null;
  const prevRate = b.signups_prev_7d > 0 ? b.signups_ordered_prev_7d / b.signups_prev_7d : null;
  const conversionTone: Tone =
    rate === null || prevRate === null ? "neutral" : rate < prevRate * 0.8 ? "attention" : "good";
  const wallets = b.wallets;
  return (
    <Block
      title="Business"
      lead="Orders, money and customers. Days are counted in UTC; 7 days = today and the six days before."
    >
      <Grid>
        <Card
          label="Orders"
          value={count(b.orders_today)}
          small={`today · ${count(b.orders_7d)} in 7 days`}
          note="Orders customers paid for and kept; failed and refunded ones are not counted."
          asOf={data.as_of}
        />
        <Card
          label="Revenue"
          value={trx(b.revenue_today_sun, 0)}
          small={`today · ${trx(b.revenue_7d_sun, 0)} in 7 days`}
          note="What customers paid for those orders, after refunds."
          asOf={data.as_of}
        />
        <Card
          label="New customers"
          value={count(b.new_customers_today)}
          small={`today · ${count(b.new_customers_7d)} in 7 days`}
          note="Counted on their first deposit, not on sign-up."
          asOf={data.as_of}
        />
        <Card
          label="Sign-up to first order"
          value={rate === null ? "—" : `${Math.round(rate * 100)} %`}
          small="7 days"
          tone={conversionTone}
          note={`${count(b.signups_ordered_7d)} of ${count(b.signups_7d)} new sign-ups placed an order.${
            prevRate === null ? "" : ` Was ${Math.round(prevRate * 100)} % the week before.`
          }`}
          asOf={data.as_of}
        />
        {wallets === null ? null : (
          <Card
            label="Money held for customers vs ours"
            value={trx(wallets.customers_sun, 0)}
            small={`held for customers · ${wallets.customers_usdt.toFixed(1)} USDT`}
            tone={moneyTone(data)}
            note={
              wallets.complete
                ? "Fine while the collector holds more than customers' balances."
                : "A wallet could not be read just now; the collector figures may be low."
            }
            asOf={wallets.as_of}
            wide
          >
            <List
              rows={[
                ["On the collector wallet", trx(wallets.collector_sun, 0)],
                ["Collector can spend (after customers' money)", trx(wallets.collector_free_sun, 0)],
              ]}
            />
          </Card>
        )}
        <Card
          label="Top customers by spend, 7 days"
          asOf={data.as_of}
          wide
          note={
            b.top_customers_7d.length === 0
              ? "No paid orders in the last 7 days."
              : b.top_share_7d === null
                ? undefined
                : `Top ${b.top_customers_7d.length} brought ${Math.round(b.top_share_7d * 100)} % of the week's revenue.`
          }
        >
          {b.top_customers_7d.length === 0 ? null : (
            <List
              rows={b.top_customers_7d.map((c, i) => [
                c.owner ?? `customer ${i + 1}`,
                `${trx(c.spent_sun, 0)} · ${count(c.orders)} orders`,
              ])}
            />
          )}
        </Card>
      </Grid>
    </Block>
  );
}

function QueueTable({ rows }: { rows: QueueCounts[] }) {
  return (
    <Table
      rows={rows.length}
      empty="The API counted no queue. The worker declares its queues on start; start the worker."
      head={
        <tr>
          <Th>Queue</Th>
          <Th numeric>Waiting</Th>
          <Th numeric>Active</Th>
          <Th numeric>Delayed</Th>
          <Th numeric>Failed</Th>
          <Th numeric>Done</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.queue}>
          <Td mono>{row.queue}</Td>
          <Td numeric class={row.waiting > 0 ? "text-warning" : ""}>{count(row.waiting)}</Td>
          <Td numeric>{count(row.active)}</Td>
          <Td numeric>{count(row.delayed)}</Td>
          <Td numeric class={row.failed > 0 ? "text-danger" : ""}>{count(row.failed)}</Td>
          <Td numeric class="text-ink-muted">{count(row.completed)}</Td>
        </tr>
      ))}
    </Table>
  );
}
